import { PortalPageContent, PortalPageContentHeader } from "@fero/layouts";
import { AppRoutes } from "@fero/utils";
import { Alert, Button, Stack } from "@mui/material";
import type { BigDecimal } from "adl-gen/common";
import type { WithDbId } from "adl-gen/common/db";
import type {
  NominatedPurchaserDetailsView,
  NomPurchaserStorefrontConfigData,
  SaveStorefrontConfigsReq,
  StorageLocationView,
  StorefrontConfigData,
  StorefrontProductAllocationData,
} from "adl-gen/ferovinum/app/api";
import type { PaymentTermsPeriod, Product } from "adl-gen/ferovinum/app/db";
import { FormikProvider, useFormik } from "formik";
import { useCallback, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { CollapsibleTagList } from "./components/collapsible-tag-list";
import { MinimumOrderCard } from "./components/minimum-order-card";
import { PaymentTermsCard } from "./components/payment-terms-card";
import { StockAllocationCard } from "./components/stock-allocation-card";
import { StorageLocationCard } from "./components/storage-location-card";

interface NewPurchaserAllocationPageViewProps {
  configs: NomPurchaserStorefrontConfigData[];
  configStorageLocation: StorageLocationView;
  orgStorageLocations: StorageLocationView[];
  allocationsWithProducts: AllocationWithProducts[];
  errorOnSaveConfig: string | null;
  nominatedPurchasersDetailsViews: NominatedPurchaserDetailsView[];
  onChangeStorageLocation: (v: StorageLocationView | null) => void;
  onSaveConfig: (req: SaveStorefrontConfigsReq) => void;
}

export interface AllocationWithProducts {
  allocationData: StorefrontProductAllocationData;
  product: WithDbId<Product> | undefined;
}

export interface SetupNewAllocationFormValues {
  configs: NomPurchaserStorefrontConfigData[];
  storageLocation: StorageLocationView;
  minimumOrder: BigDecimal | null;
  paymentTerms: PaymentTermsPeriod | null;
  allocationsWithProducts: AllocationWithProducts[];
}

const FooterToolbar = ({
  onNavigateBackToDashboard,
}: {
  onNavigateBackToDashboard: () => void;
}) => {
  return (
    <Stack direction="row" spacing={2} justifyContent="flex-end">
      <Button
        onClick={onNavigateBackToDashboard}
        variant="outlined"
        color="primary"
      >
        Back
      </Button>
      <Button variant="contained" color="primary" type="submit">
        Save New Allocation
      </Button>
    </Stack>
  );
};

const BuildNewConfigAndAllocations = (
  values: SetupNewAllocationFormValues,
): SaveStorefrontConfigsReq => {
  const newConfig: StorefrontConfigData = {
    storefrontEnabled: true,
    storageLocationId: values.storageLocation.id,
    paymentTerm: values.paymentTerms ?? {
      kind: "daysAfterCollection",
      value: 0,
    }, // TODO MB : have some safety on defaults
    minimumOrderValue: values.minimumOrder ?? ("0.0" as BigDecimal),
  };

  const newAllocations: StorefrontProductAllocationData[] | null = values
    .allocationsWithProducts.length
    ? values.allocationsWithProducts.map(
        (a: AllocationWithProducts) => a.allocationData,
      )
    : null;

  return {
    config: newConfig,
    allocations: newAllocations,
    nominatedPurchaserIds: values.configs.map(
      (p: NomPurchaserStorefrontConfigData) => p.nominatedPurchaserId,
    ),
  } as SaveStorefrontConfigsReq;
};

export const NewPurchaserAllocationPageView = ({
  configs: initialConfigsData,
  configStorageLocation,
  orgStorageLocations,
  allocationsWithProducts,
  errorOnSaveConfig,
  nominatedPurchasersDetailsViews,
  onChangeStorageLocation,
  onSaveConfig,
}: NewPurchaserAllocationPageViewProps) => {
  const history = useHistory();
  const isSingleConfig = initialConfigsData.length === 1;
  const [selectedFile, setSelectedFile] = useState<File>();

  const initialValues = useMemo(
    () => ({
      configs: initialConfigsData,
      storageLocation: configStorageLocation,
      minimumOrder: isSingleConfig
        ? initialConfigsData[0].config.minimumOrderValue
        : null,
      paymentTerms: isSingleConfig
        ? initialConfigsData[0].config.paymentTerm
        : null,
      allocationsWithProducts:
        initialConfigsData.length === 1 ? allocationsWithProducts : [],
    }),
    [
      initialConfigsData,
      configStorageLocation,
      isSingleConfig,
      allocationsWithProducts,
    ],
  );

  const form = useFormik<SetupNewAllocationFormValues>({
    initialValues: initialValues,
    validateOnMount: true,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: (values) => {
      onSaveConfig(BuildNewConfigAndAllocations(values));
    },
  });

  const handleRemovePurchaser = useCallback(
    (name: string) => {
      form.setFieldValue(
        "configs",
        form.values.configs.filter(
          (purchaser) => purchaser.nominatedPurchaserId !== name,
        ),
      );
    },
    [form],
  );

  const onNavigateBackToDashboard = () => {
    history.push(AppRoutes.Storefront);
  };

  return (
    <PortalPageContent
      header={<PortalPageContentHeader title="New Allocation" />}
    >
      <FormikProvider value={form}>
        <form onSubmit={form.handleSubmit}>
          <Stack spacing={4}>
            <CollapsibleTagList
              tags={form.values.configs.map((pur) => {
                return {
                  tagId: pur.nominatedPurchaserId,
                  name:
                    nominatedPurchasersDetailsViews.find(
                      (n) =>
                        n.nominatedPurchaserId === pur.nominatedPurchaserId,
                    )?.name || pur.nominatedPurchaserId,
                };
              })}
              onDeleteTag={handleRemovePurchaser}
              title="For the following purchasers:"
              onLastElementDeleted={onNavigateBackToDashboard}
            />
            <StorageLocationCard
              activeStorageLocation={configStorageLocation}
              storageLocations={orgStorageLocations}
              onChangeLocation={onChangeStorageLocation}
            />
            <MinimumOrderCard form={form} />
            <PaymentTermsCard form={form} />
            <StockAllocationCard
              form={form}
              selectedFile={selectedFile}
              setSelectedFile={setSelectedFile}
            />
            {errorOnSaveConfig && (
              <Alert severity="error">{errorOnSaveConfig}</Alert>
            )}
            <FooterToolbar
              onNavigateBackToDashboard={onNavigateBackToDashboard}
            />
          </Stack>
        </form>
      </FormikProvider>
    </PortalPageContent>
  );
};
